import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  getAdminUsers,
  deleteAdminUser,
  postCancelDeleteUser,
  postCreateUser,
  postToggleAdmin,
  postExtendUserPlan,
  postUpdateUserPlan,
} from 'utils/api/admin';

const PLAN_LABEL = { free: '무료', basic: '1단계', premium: '2단계' };
const PLAN_OPTIONS = [
  { value: 'free', label: '무료' },
  { value: 'basic', label: '1단계' },
  { value: 'premium', label: '2단계' },
];

const Users = () => {
  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const size = 20;
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    password: '',
    plan: 'free',
    planExpiredAt: '',
  });
  const [planTarget, setPlanTarget] = useState(null);
  const [planForm, setPlanForm] = useState({ plan: 'free', planExpiredAt: '' });
  const [extendTarget, setExtendTarget] = useState(null);
  const [extendUntil, setExtendUntil] = useState('');

  useEffect(() => {
    fetchUsers();
  }, [page]);

  const fetchUsers = async (searchValue) => {
    try {
      const res = await getAdminUsers({
        search: searchValue !== undefined ? searchValue : search,
        page,
        size,
      });
      setUsers(res.results.users);
      setTotal(res.results.total);
    } catch (err) {
      console.error(err);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(search);
  };

  const handleDormant = async (email) => {
    if (!window.confirm('해당 유저를 휴면 처리하시겠습니까?')) return;
    try {
      await deleteAdminUser(email);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleReactivate = async (email) => {
    if (!window.confirm('해당 유저를 휴면 해제하시겠습니까?')) return;
    try {
      await postCancelDeleteUser(email);
      fetchUsers();
    } catch (err) {
      console.error(err);
    }
  };

  const handleToggleAdmin = async (userId, currentIsAdmin) => {
    const action = currentIsAdmin ? '어드민 권한을 박탈' : '어드민 권한을 부여';
    if (!window.confirm(`해당 유저의 ${action}하시겠습니까?`)) return;
    try {
      await postToggleAdmin(userId, !currentIsAdmin);
      fetchUsers();
    } catch (err) {
      alert(err?.response?.data?.message || '권한 변경에 실패했습니다.');
    }
  };

  const openEditPlan = (u) => {
    setPlanTarget(u);
    setPlanForm({
      plan: u.plan || 'free',
      planExpiredAt: u.planExpiredAt
        ? new Date(u.planExpiredAt).toISOString().slice(0, 10)
        : '',
    });
  };

  const handleUpdatePlan = async (e) => {
    e.preventDefault();
    if (!planTarget) return;
    try {
      await postUpdateUserPlan(planTarget._id, {
        plan: planForm.plan,
        planExpiredAt:
          planForm.plan !== 'free' && planForm.planExpiredAt
            ? planForm.planExpiredAt
            : undefined,
      });
      setPlanTarget(null);
      fetchUsers();
    } catch (err) {
      alert(err?.response?.data?.message || '플랜 변경에 실패했습니다.');
    }
  };

  const openExtend = (u) => {
    setExtendTarget(u);
    const base = u.planExpiredAt
      ? new Date(u.planExpiredAt)
      : new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
    setExtendUntil(base.toISOString().slice(0, 10));
  };

  const handleExtend = async (e) => {
    e.preventDefault();
    if (!extendTarget) return;
    if (!extendUntil) {
      alert('만료일을 선택해주세요.');
      return;
    }
    try {
      await postExtendUserPlan(extendTarget._id, { untilDate: extendUntil });
      setExtendTarget(null);
      setExtendUntil('');
      fetchUsers();
    } catch (err) {
      alert(err?.response?.data?.message || '만료일 연장에 실패했습니다.');
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await postCreateUser({
        name: form.name,
        email: form.email,
        password: form.password,
        plan: form.plan,
        planExpiredAt:
          form.plan !== 'free' && form.planExpiredAt
            ? form.planExpiredAt
            : undefined,
      });
      setShowModal(false);
      setForm({
        name: '',
        email: '',
        password: '',
        plan: 'free',
        planExpiredAt: '',
      });
      fetchUsers();
    } catch (err) {
      alert(err?.response?.data?.message || '유저 등록에 실패했습니다.');
    }
  };

  const totalPages = Math.ceil(total / size);
  const formatDate = (d) => (d ? new Date(d).toLocaleDateString('ko-KR') : '-');

  return (
    <Container>
      <Header>
        <Title>유저 관리</Title>
        <AddButton onClick={() => setShowModal(true)}>+ 유저 등록</AddButton>
      </Header>
      <SearchForm onSubmit={handleSearch}>
        <SearchInput
          placeholder="이메일 또는 이름 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <SearchButton type="submit">검색</SearchButton>
      </SearchForm>
      <Info>총 {total}명</Info>
      <Table>
        <thead>
          <tr>
            <Th>이름</Th>
            <Th>이메일</Th>
            <Th>플랜</Th>
            <Th>만료일</Th>
            <Th>가입일</Th>
            <Th>최근 접속</Th>
            <Th>어드민</Th>
            <Th>상태</Th>
            <Th>관리</Th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u._id}>
              <Td>{u.name}</Td>
              <Td>{u.email}</Td>
              <Td>
                <PlanBadge plan={u.plan || 'free'}>
                  {PLAN_LABEL[u.plan] || '무료'}
                </PlanBadge>
              </Td>
              <Td>{formatDate(u.planExpiredAt)}</Td>
              <Td>{formatDate(u.createdAt)}</Td>
              <Td>{formatDate(u.lastLoginAt)}</Td>
              <Td>
                <BadgeGroup>
                  <RoleBadge
                    isAdmin={u.isAdmin}
                    onClick={() => handleToggleAdmin(u._id, u.isAdmin)}
                  >
                    {u.isAdmin ? '어드민' : '일반'}
                  </RoleBadge>
                  {u.deleted ? (
                    <DormantBadge>휴면</DormantBadge>
                  ) : (
                    <ActiveBadge>정상</ActiveBadge>
                  )}
                </BadgeGroup>
              </Td>
              <Td>
                <ActionRow>
                  <ActionBtn onClick={() => openEditPlan(u)}>플랜</ActionBtn>
                  {u.plan && u.plan !== 'free' && (
                    <ActionBtn onClick={() => openExtend(u)}>연장</ActionBtn>
                  )}
                  {u.deleted ? (
                    <ActionBtn onClick={() => handleReactivate(u.email)}>
                      휴면해제
                    </ActionBtn>
                  ) : (
                    <ActionBtn dormant onClick={() => handleDormant(u.email)}>
                      휴면
                    </ActionBtn>
                  )}
                </ActionRow>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
      {totalPages > 1 && (
        <Pagination>
          <PageBtn disabled={page <= 1} onClick={() => setPage(page - 1)}>
            이전
          </PageBtn>
          <PageInfo>
            {page} / {totalPages}
          </PageInfo>
          <PageBtn
            disabled={page >= totalPages}
            onClick={() => setPage(page + 1)}
          >
            다음
          </PageBtn>
        </Pagination>
      )}

      {planTarget && (
        <Overlay onClick={() => setPlanTarget(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>플랜 수동 변경</ModalTitle>
            <ModalInfo>
              {planTarget.name} · {planTarget.email}
            </ModalInfo>
            <Form onSubmit={handleUpdatePlan}>
              <Label>플랜</Label>
              <Select
                value={planForm.plan}
                onChange={(e) =>
                  setPlanForm({ ...planForm, plan: e.target.value })
                }
              >
                {PLAN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              {planForm.plan !== 'free' && (
                <>
                  <Label>만료일</Label>
                  <Input
                    type="date"
                    required
                    value={planForm.planExpiredAt}
                    onChange={(e) =>
                      setPlanForm({
                        ...planForm,
                        planExpiredAt: e.target.value,
                      })
                    }
                  />
                </>
              )}
              <ModalActions>
                <CancelBtn type="button" onClick={() => setPlanTarget(null)}>
                  취소
                </CancelBtn>
                <SubmitBtn type="submit">저장</SubmitBtn>
              </ModalActions>
            </Form>
          </ModalBox>
        </Overlay>
      )}

      {extendTarget && (
        <Overlay onClick={() => setExtendTarget(null)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>플랜 만료일 연장</ModalTitle>
            <ModalInfo>
              {extendTarget.name} · {extendTarget.email}
              <br />
              현재 만료일: {formatDate(extendTarget.planExpiredAt)}
            </ModalInfo>
            <Form onSubmit={handleExtend}>
              <Label>새 만료일</Label>
              <Input
                type="date"
                required
                value={extendUntil}
                onChange={(e) => setExtendUntil(e.target.value)}
              />
              <HelperText>
                선택한 날짜로 만료일이 설정됩니다. 활성 구독이 있으면 구독
                기간도 함께 갱신됩니다.
              </HelperText>
              <ModalActions>
                <CancelBtn type="button" onClick={() => setExtendTarget(null)}>
                  취소
                </CancelBtn>
                <SubmitBtn type="submit">적용</SubmitBtn>
              </ModalActions>
            </Form>
          </ModalBox>
        </Overlay>
      )}

      {showModal && (
        <Overlay onClick={() => setShowModal(false)}>
          <ModalBox onClick={(e) => e.stopPropagation()}>
            <ModalTitle>유저 수동 등록</ModalTitle>
            <Form onSubmit={handleCreateUser}>
              <Label>이름</Label>
              <Input
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Label>이메일</Label>
              <Input
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
              <Label>비밀번호</Label>
              <Input
                required
                type="password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
              <Label>플랜</Label>
              <Select
                value={form.plan}
                onChange={(e) => setForm({ ...form, plan: e.target.value })}
              >
                {PLAN_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </Select>
              {form.plan !== 'free' && (
                <>
                  <Label>플랜 종료일</Label>
                  <Input
                    type="date"
                    required
                    value={form.planExpiredAt}
                    onChange={(e) =>
                      setForm({ ...form, planExpiredAt: e.target.value })
                    }
                  />
                </>
              )}
              <ModalActions>
                <CancelBtn type="button" onClick={() => setShowModal(false)}>
                  취소
                </CancelBtn>
                <SubmitBtn type="submit">등록</SubmitBtn>
              </ModalActions>
            </Form>
          </ModalBox>
        </Overlay>
      )}
    </Container>
  );
};

const Container = styled.div``;
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;
const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  color: #232d42;
  margin: 0;
`;
const AddButton = styled.button`
  padding: 8px 20px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #5338d4;
  }
`;
const SearchForm = styled.form`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;
const SearchInput = styled.input`
  padding: 8px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  width: 300px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const SearchButton = styled.button`
  padding: 8px 20px;
  background: #644ded;
  color: #fff;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  cursor: pointer;
  &:hover {
    background: #5338d4;
  }
`;
const Info = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-bottom: 12px;
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
`;
const Th = styled.th`
  text-align: left;
  padding: 12px 16px;
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  border-bottom: 1px solid #e4e4e4;
  background: #fafafa;
`;
const Td = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: #232d42;
  border-bottom: 1px solid #f0f0f0;
`;
const PlanBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: ${({ plan }) =>
    plan === 'premium' ? '#f0edff' : plan === 'basic' ? '#e6f7ff' : '#f0f0f0'};
  color: ${({ plan }) =>
    plan === 'premium' ? '#644ded' : plan === 'basic' ? '#1890ff' : '#8f9bba'};
`;
const Pagination = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 20px;
`;
const PageBtn = styled.button`
  padding: 6px 16px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 13px;
  cursor: pointer;
  &:disabled {
    opacity: 0.4;
    cursor: default;
  }
`;
const PageInfo = styled.span`
  font-size: 13px;
  color: #8f9bba;
`;
const BadgeGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;
const RoleBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  background: ${({ isAdmin }) => (isAdmin ? '#f0edff' : '#f0f0f0')};
  color: ${({ isAdmin }) => (isAdmin ? '#644ded' : '#8f9bba')};
  &:hover {
    opacity: 0.7;
  }
`;
const DormantBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #fff3cd;
  color: #856404;
`;
const ActiveBadge = styled.span`
  padding: 4px 10px;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 600;
  background: #d4edda;
  color: #155724;
`;
const ActionRow = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
`;
const ActionBtn = styled.button`
  padding: 4px 12px;
  border-radius: 6px;
  border: 1px solid ${({ dormant }) => (dormant ? '#dc3545' : '#28a745')};
  background: #fff;
  color: ${({ dormant }) => (dormant ? '#dc3545' : '#28a745')};
  font-size: 12px;
  cursor: pointer;
  &:hover {
    background: ${({ dormant }) => (dormant ? '#dc3545' : '#28a745')};
    color: #fff;
  }
`;
const ModalInfo = styled.div`
  font-size: 13px;
  color: #8f9bba;
  margin-bottom: 16px;
  line-height: 1.6;
`;
const HelperText = styled.div`
  font-size: 12px;
  color: #8f9bba;
  margin-top: 4px;
`;
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
const ModalBox = styled.div`
  background: #fff;
  border-radius: 16px;
  padding: 32px;
  width: 420px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
`;
const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #232d42;
  margin: 0 0 24px 0;
`;
const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Label = styled.label`
  font-size: 13px;
  font-weight: 600;
  color: #8f9bba;
  margin-top: 4px;
`;
const Input = styled.input`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const Select = styled.select`
  padding: 10px 12px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  &:focus {
    border-color: #644ded;
  }
`;
const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
`;
const CancelBtn = styled.button`
  padding: 10px 24px;
  border: 1px solid #e4e4e4;
  border-radius: 8px;
  background: #fff;
  font-size: 14px;
  cursor: pointer;
`;
const SubmitBtn = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 8px;
  background: #644ded;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  &:hover {
    background: #5338d4;
  }
`;

export default Users;
